import React from 'react';
import { post, postJson, userAction } from '@/util/helpers/ajax-helpers';
import { userIsFinishedOnboarding } from '@/components/HighlightFeature';
import { loading, completed, standardErrorHandler } from './helpers';
import {
  LAYER_USER_INTERACTION,
  MODAL_SAVE_CLICKED,
  SAVE_LAYERS_CLICKED,
  MODAL_DATA_LOADED,
  TABLE_SELECTED,
  TEMPLATE_LAYER_ADDED,
  START_EDITING_LAYER_INLINE,
  START_NEW_QUERY,
  COLUMNS_DATA_LOADED,
  INLINE_LAYER_ADDED,
} from './constants';
import {
  JSON_ARRAY,
  JSON_OBJECT,
  SQL_ARRAY,
} from '../util/enums/EnumExpandType';
import {
  FROM,
  EXPAND,
  AGGREGATE,
  FROM_SAVED_QUERY,
} from '../util/enums/EnumLayers';
import ModalFrom from '../components/modals/ModalFrom';
import ModalSqlEditor from '../components/modals/ModalSqlEditor';
import ModalCsvUpload from '../components/modals/ModalCsvUpload';
import ModalSelect from '../components/modals/ModalSelect';
import ModalJoin from '../components/modals/ModalJoin';
import ModalAddColumn from '../components/modals/ModalAddColumn';
import UpgradeModal from '../components/modals/UpgradeModal';
import ModalAggregate from '../components/modals/ModalAggregate';
import InlineAggregateLayer from '../components/layers/InlineAggregateLayer';
import TemplateLayer from '../components/layers/TemplateLayer';
import { openModal } from './actions-modals';
import { loadColumnsRows } from './actions-rows';
import { validateExpression } from './actions-custom-columns';
import { openReactModal } from '.';
import AggregateColumn from '../util/AggregateColumn';
import AggregateFunction from '../util/AggregateFunction';
import { COUNT_UNIQUE } from '../util/AggregateOperation';

export function tableSelected(table) {
  return (dispatch, getState) => {
    const state = getState();
    const isNewWorld = state.datasource.newWorld;
    const hasCreatedAt = !!state.datasource.tablesWithCreatedAt.find((t) =>
      t.equals(table),
    );
    const doingOnboarding = !userIsFinishedOnboarding(
      state.datasource.userActions,
    );
    dispatch(
      completed(TABLE_SELECTED, {
        table,
        hasCreatedAt,
        isNewWorld,
        doingOnboarding,
      }),
    );
  };
}

export function addTemplateLayer(layerType, layerToAppendTo = null) {
  return (dispatch) => {
    const newLayer =
      layerType === AGGREGATE
        ? new InlineAggregateLayer()
        : new TemplateLayer(layerType);

    dispatch(
      completed(TEMPLATE_LAYER_ADDED, {
        newLayer,
        layerToAppendTo,
      }),
    );
  };
}

export function addInlineLayer(modal, columnName, additionalOptions) {
  return (dispatch) => {
    // TODO: handle other layer types (currently just supports InlineAggregateLayer)
    const isGroupBy = !!columnName;
    return dispatch(
      completed(INLINE_LAYER_ADDED, {
        modal,
        state: {
          isNewWorld: true,
          selected: isGroupBy
            ? [
                AggregateColumn.fromColumn(
                  columnName,
                  additionalOptions.aggregation,
                ),
              ]
            : [],
          aggFunctions: isGroupBy
            ? [new AggregateFunction(COUNT_UNIQUE, null)]
            : [
                new AggregateFunction(
                  additionalOptions.selectedOp,
                  additionalOptions.columnName,
                ),
              ],
          extraColumns: [],
          pks: [],
        },
      }),
    );
  };
}

export function startNewQuery() {
  userAction('use-start-new-query');
  return (dispatch) => {
    dispatch(completed(START_NEW_QUERY));
  };
}

export function addLayerClicked(modal, previousLayers, additionalOptions) {
  return loadLayerModal(modal, previousLayers, null, null, additionalOptions);
}

export function editLayerClicked(layerBeingEdited, previousLayers) {
  if (!layerBeingEdited) {
    // no layer being edited
    return loading(START_EDITING_LAYER_INLINE, { layer: null });
  }
  if (!layerBeingEdited.editMode) {
    // check if layer supports inline edit mode
    // load modal
    return loadLayerModal(
      layerBeingEdited.modal(),
      previousLayers,
      layerBeingEdited,
    );
  }
  // move to edit mode, and load columns for it
  return loadColumnsForLayerEditing(layerBeingEdited, previousLayers);
}

function loadColumnsForLayerEditing(layerBeingEdited, previousLayers) {
  return (dispatch) => {
    dispatch(loading(START_EDITING_LAYER_INLINE, { layer: layerBeingEdited }));
    if ([FROM, FROM_SAVED_QUERY].includes(layerBeingEdited.type())) {
      // FROM layers don't need any columns
      dispatch(
        completed(START_EDITING_LAYER_INLINE, {
          layer: layerBeingEdited,
          json: {},
        }),
      );
      return;
    }
    // all other layers need a columns list
    post('loading query steps', '/columns?includeHidden=true', previousLayers)
      .then(function (json) {
        dispatch(
          completed(START_EDITING_LAYER_INLINE, {
            layer: layerBeingEdited,
            json,
          }),
        );
      })
      .catch(standardErrorHandler(MODAL_DATA_LOADED, dispatch));
  };
}

export function layerUserInteraction(props) {
  return completed(LAYER_USER_INTERACTION, props);
}

export function createLayerFromColumn(
  type,
  previousLayers,
  columnName,
  additionalOptions,
) {
  switch (type) {
    case SQL_ARRAY:
    case JSON_ARRAY:
    case JSON_OBJECT: {
      const state = { columnName, expandType: type };
      return completed(MODAL_SAVE_CLICKED, {
        modal: EXPAND,
        state,
        existingLayer: null,
      });
    }
    default:
      return loadLayerModal(
        type,
        previousLayers,
        null,
        columnName,
        additionalOptions,
      );
  }
}

export function loadColumns(relevantLayers, modal) {
  return (dispatch) => {
    dispatch(loading(COLUMNS_DATA_LOADED, { modal }));
    postJson('/columns?includeHidden=false', relevantLayers)
      .then(function (json) {
        dispatch(
          completed(COLUMNS_DATA_LOADED, { modal, json: json._columns }),
        );
      })
      .catch(standardErrorHandler(COLUMNS_DATA_LOADED, dispatch));
  };
}

export function exportAsSql(activeLayers) {
  return (dispatch) => {
    dispatch(loading(MODAL_DATA_LOADED, { modal: ModalSqlEditor }));
    postJson('/exportSql', { _layers: activeLayers })
      .then(function (json) {
        userAction('use-sql-export');
        dispatch(
          completed(MODAL_DATA_LOADED, {
            modal: ModalSqlEditor,
            props: { isExport: true, searchedSql: json._sql },
          }),
        );
      })
      .catch(standardErrorHandler(MODAL_DATA_LOADED, dispatch));
  };
}

export function saveLayersClicked(
  activeLayers,
  inactiveLayers,
  viewBeingEdited,
) {
  return (dispatch, getState) => {
    const currentState = getState();
    const savingExisting = viewBeingEdited ? 1 : 0;
    const { maxSteps } = currentState.datasource; // HACK: using maxSteps as the number of saved queries that are allowed
    if (currentState.datasource.totalSavedViews >= maxSteps + savingExisting) {
      dispatch(openReactModal(<UpgradeModal maxQueries={maxSteps} />));
      return;
    }

    return dispatch(
      completed(SAVE_LAYERS_CLICKED, {
        activeLayers,
        inactiveLayers,
        chartConfig: currentState.layers.chartingOptions,
        viewBeingEdited,
        metricsDimensionConfig:
          currentState.metricConfigSlice.metricsDimensionConfiguration,
      }),
    );
  };
}

function loadLayerModal(
  modal,
  relevantLayers,
  existingLayer,
  relevantColumnName,
  additionalOptions,
) {
  return (dispatch, getState) => {
    // SQL editor and CSV Upload require no further loading
    if ([ModalSqlEditor, ModalCsvUpload, ModalFrom].includes(modal)) {
      const allTables = getState().datasource.tables;
      dispatch(
        openModal(modal, {
          existing: existingLayer,
          allTables,
        }),
      );
      return;
    }

    // SELECT layer requires additional loading
    if (modal === ModalSelect) {
      dispatch(loadColumnsRows(relevantLayers));
    }

    // load all the metadata needed by a typical modal
    const includeHidden =
      modal === ModalSelect || modal === ModalJoin || modal === ModalAggregate;
    dispatch(loading(MODAL_DATA_LOADED, { modal }));
    postJson(`/columns?includeHidden=${includeHidden}`, relevantLayers)
      .then(function (json) {
        dispatch(
          completed(MODAL_DATA_LOADED, {
            modal,
            json: json._columns,
            isLayerModal: true,
            existingLayer,
            relevantLayers,
            relevantColumnName,
            selectedColumns: json._selected,
            pks: json._pks,
            additionalOptions,
          }),
        );

        if (modal === ModalAddColumn) {
          if (existingLayer) {
            dispatch(
              validateExpression(
                json._columns,
                existingLayer.expression(),
                existingLayer.replaceExisting(),
              ),
            );
          } else if (additionalOptions && additionalOptions.expression) {
            dispatch(
              validateExpression(
                json._columns,
                additionalOptions.expression,
                false,
              ),
            );
          }
        }
      })
      .catch(standardErrorHandler(MODAL_DATA_LOADED, dispatch));
  };
}
